import React, { useState } from "react";
import { buildAddInspectionItem } from "../../api/shape.js";
import { CATEGORIES, STAGES_BY_CATEGORY } from "../../api/stageVocabulary.js";

// Side buttons in CIS Capture's order (P, S, none) with "P and S" for a booking that covers both sides.
const SIDES = [
  { value: "P", label: "P" },
  { value: "S", label: "S" },
  { value: "PS", label: "P and S" },
  { value: "", label: "None" },
];
const STAGE_OTHER = "__other__";

/** The category buttons' wording: CIS Capture's, "Outfitting" shortened to "outf.". */
export function categoryLabel(category) {
  const short = category.replace(" Outfitting", " outf.");
  return short.charAt(0) + short.slice(1).toLowerCase();
}

/**
 * "Add one inspection" (Today): the same inputs as CIS Capture's New record
 * (inspection item, side, block, category, stage) plus time and place, and no
 * result, defects, parties or photos: it is a booking, not a record. Saves
 * through the existing schedule upsert (App.jsx#handleAddInspection), with
 * category and stage so the inspector's capture form prefills. `phone` swaps
 * in the phone form controls (touch size). Validation is
 * shape.js#buildAddInspectionItem, the same function the tests exercise.
 */
export default function AddInspection({ projects, date, onSave, onClose, phone = false }) {
  const [values, setValues] = useState({ code: projects[0] || "", item: "", side: "", block: "", category: "", stage: "", stageOther: "", time: "", place: "" });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const set = (k) => (e) => setValues((v) => ({ ...v, [k]: e.target.value }));
  const stages = STAGES_BY_CATEGORY[values.category] || [];

  async function submit(e) {
    if (e && e.preventDefault) e.preventDefault();
    const stage = values.stage === STAGE_OTHER ? values.stageOther : values.stage;
    const built = buildAddInspectionItem({ ...values, stage }, { date });
    if (!built.ok) {
      setErrors(built.errors);
      return;
    }
    setErrors({});
    setSaving(true);
    const result = await onSave(built.code, built.items);
    setSaving(false);
    if (result && result.ok) onClose();
    else setMessage((result && result.message) || "Could not save");
  }

  const C = phone
    ? { field: "ph-dy-field", input: "ph-input", select: "ph-select", err: "ph-small", btn: "ph-btn pri", off: "ph-btn", grid: "ph-stack", tiles: "ph-radiogrid ph-addtiles", cats: "ph-radiogrid ph-addtiles two", tile: "ph-radio", on: "ph-radio on", legend: "ph-addlegend" }
    : { field: "sf-field", input: "", select: "", err: "sf-err", btn: "btn acc", off: "btn", grid: "sf-grid", tiles: "sf-tiles", cats: "sf-tiles two", tile: "sf-tile", on: "sf-tile on", legend: "sf-legend" };

  const tile = (group, value, label) => {
    const on = values[group] === value;
    return (
      <button
        key={label}
        type="button"
        role="radio"
        aria-checked={on ? "true" : "false"}
        className={on ? C.on : C.tile}
        data-testid={`add-${group}-${label}`}
        onClick={() => setValues((v) => (group === "category" && v.category !== value ? { ...v, category: value, stage: "", stageOther: "" } : { ...v, [group]: value }))}
      >
        {label}
      </button>
    );
  };

  const fields = (
    <div className={C.grid} style={phone ? { gap: 14 } : undefined}>
      {projects.length > 1 ? (
        <div className={C.field}>
          <label htmlFor="add-code">Project</label>
          <select id="add-code" aria-label="Project" className={C.select} value={values.code} onChange={set("code")} aria-invalid={errors.code ? "true" : undefined}>
            {projects.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      ) : null}
      <div className={C.field}>
        <label htmlFor="add-item">Inspection item · block / system</label>
        <input id="add-item" aria-label="Inspection item · block / system" type="text" className={C.input} value={values.item} maxLength={255} placeholder="BS14 (P)" autoComplete="off" onChange={set("item")} aria-invalid={errors.item ? "true" : undefined} />
        {errors.item ? <span className={C.err}>{errors.item}</span> : null}
      </div>
      <fieldset className={`${C.field} add-fieldset`}>
        <legend className={C.legend}>Side</legend>
        <div className={C.tiles} role="radiogroup" aria-label="Side">
          {SIDES.map((s) => tile("side", s.value, s.label))}
        </div>
        {errors.side ? <span className={C.err}>{errors.side}</span> : null}
      </fieldset>
      <div className={C.field}>
        <label htmlFor="add-block">Block (optional)</label>
        <input id="add-block" aria-label="Block (optional)" type="text" className={C.input} value={values.block} maxLength={32} placeholder="UD10" autoComplete="off" autoCapitalize="characters" onChange={set("block")} aria-invalid={errors.block ? "true" : undefined} />
        {errors.block ? <span className={C.err}>{errors.block}</span> : null}
      </div>
      <fieldset className={`${C.field} add-fieldset`}>
        <legend className={C.legend}>Category</legend>
        <div className={C.cats} role="radiogroup" aria-label="Category">
          {CATEGORIES.map((c) => tile("category", c, categoryLabel(c)))}
        </div>
        {errors.category ? <span className={C.err}>{errors.category}</span> : null}
      </fieldset>
      <div className={C.field}>
        <label htmlFor="add-stage">Stage</label>
        <select id="add-stage" aria-label="Stage" className={C.select} value={values.stage} disabled={!values.category} onChange={set("stage")} aria-invalid={errors.stage ? "true" : undefined}>
          <option value="">{values.category ? "Select stage" : "Choose a category first"}</option>
          {stages.map((st) => <option key={st} value={st}>{st}</option>)}
          {values.category ? <option value={STAGE_OTHER}>Other — not listed</option> : null}
        </select>
        {values.stage === STAGE_OTHER ? (
          <input id="add-stage-other" type="text" aria-label="Type the stage" className={C.input} value={values.stageOther} maxLength={255} placeholder="Type the stage" autoComplete="off" onChange={set("stageOther")} aria-invalid={errors.stage ? "true" : undefined} />
        ) : null}
        {errors.stage ? <span className={C.err}>{errors.stage}</span> : null}
      </div>
      <div className={C.field}>
        <label htmlFor="add-time">Time</label>
        <input id="add-time" aria-label="Time" type="time" className={C.input} value={values.time} onChange={set("time")} aria-invalid={errors.time ? "true" : undefined} />
        {errors.time ? <span className={C.err}>{errors.time}</span> : null}
      </div>
      <div className={C.field}>
        <label htmlFor="add-place">Place</label>
        <input id="add-place" aria-label="Place" type="text" className={C.input} value={values.place} maxLength={255} onChange={set("place")} aria-invalid={errors.place ? "true" : undefined} />
        {errors.place ? <span className={C.err}>{errors.place}</span> : null}
      </div>
    </div>
  );

  if (phone) {
    return (
      <form data-testid="day-add-form" onSubmit={submit} className="ph-stack" style={{ gap: 14 }}>
        {fields}
        {message ? <span role="alert" className="ph-small" style={{ color: "var(--ph-bad)" }}>{message}</span> : null}
        <button type="submit" className={C.btn} disabled={saving}>{saving ? "Saving…" : "Add to Today"}</button>
        <button type="button" className={C.off} onClick={onClose}>Cancel</button>
      </form>
    );
  }
  return (
    <form className="card sf" data-testid="day-add-form" onSubmit={submit}>
      <div className="t">Add one inspection</div>
      {fields}
      {message ? <div role="alert" className="sf-err">{message}</div> : null}
      <div style={{ display: "flex", gap: 8 }}>
        <button type="submit" className={C.btn} disabled={saving}>{saving ? "Saving…" : "Add to Today"}</button>
        <button type="button" className={C.off} onClick={onClose}>Cancel</button>
      </div>
    </form>
  );
}
